/**
 * @file Provides a Higher-Order Component (HOC) to wrap the main application with Auth0 authentication functionalities
 * and integrates with the react-router for navigation after redirections. This module is located in the `src` directory
 * because it's a source file that's essential for the main functionality of the application, particularly in handling
 * authentication redirects seamlessly with react-router's navigation.
 * Inspired by: https://developer.auth0.com/resources/guides/spa/react/basic-authentication#configure-a-react-authentication-provider
 */

import { useNavigate } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import React from 'react';
import { getAuth0Config } from './configUtils';
import PropTypes from 'prop-types';

/**
 * A Higher-Order Component that wraps the application or a part of it with Auth0 authentication functionalities.
 * This component initializes Auth0 with provided environment configurations and integrates with react-router
 * to handle post-authentication navigation based on the application state.
 *
 * @function
 * @name Auth0ProviderWithNavigate
 * @param {Object} props - The component properties.
 * @param {ReactNode} props.children - React children components or nodes to be rendered within the Auth0 context.
 * @returns {ReactElement} The wrapped children within the Auth0 provider context.
 */
export const Auth0ProviderWithNavigate = ({ children }) => {
    const navigate = useNavigate();

    const auth0Config = getAuth0Config();

    const onRedirectCallback = (appState) => {
        navigate(appState?.returnTo || window.location.pathname);
    };

    if (!auth0Config) {
        console.error('Auth0 configuration is missing');
        return null;
    }

    // Destructure the configuration properties
    const { domain, clientId, redirectUri, audience, scope } = auth0Config;

    return (
        <Auth0Provider
            domain={domain}
            clientId={clientId}
            useRefreshTokens={true}
            useRefreshTokensFallback={true}
            cacheLocation='localstorage'
            authorizationParams={{
                redirect_uri: redirectUri,
                audience: audience,
                scope: scope
            }}
            onRedirectCallback={onRedirectCallback}
        >
            {children}
        </Auth0Provider>
    );
};

Auth0ProviderWithNavigate.propTypes = {
    children: PropTypes.node.isRequired
};
